/**
 * Stage 5: attach doors / windows / openings to the room's perimeter surfaces.
 *
 * Every host of the built room with a line (`lineHosts`: the four primary
 * edges, each alcove's three walls and two corner diagonals, and any
 * room-corner chamfers) becomes a candidate LineFrame. Each raw opening
 * surface attaches to the frame with the smallest error (perpendicular
 * distance + overhang past the frame's ends); openings that merely re-state
 * an alcove's open mouth are absorbed.
 */

import { addSegmentsInPositionOrder } from '../edits';
import { lineCoords, LineFrame } from '../geometry';
import { type LineEdgeRef, lineHosts, resolveLineRef } from '../refs';
import { DoorSwing, EdgeSegmentType, FloorPlanEdgeSegmentSimple, FloorPlanRoom, Point, Surface } from '../schemas';
import { ConversionOptions, ConversionReport, FT_PER_M, RoomKind } from './conversionTypes';
import { toFeetPoint } from './normalizeWalls';

/** A standard US house front door is 3 ft; anything meaningfully wider than
 *  that scanned as one door is far more likely a double door, or a closet. */
const WIDE_DOOR_MIN_WIDTH = 3.2;

/**
 * The scan gives a door's width but not how it opens, so this guesses from the
 * room. A wide door in a bedroom is usually the closet's bifold. Hallway doors
 * open out, into the rooms off the hall. Anywhere else a wide door is a double
 * door and a single door keeps the default (undefined, read as swinging in).
 */
const doorSwing = (width: number, roomKind: RoomKind | undefined): DoorSwing | undefined => {
  const wide = width > WIDE_DOOR_MIN_WIDTH;
  if (roomKind === 'bedroom' && wide) return 'bifold';
  if (roomKind === 'hallway') return wide ? 'pairOut' : 'outLeft';
  return wide ? 'pairIn' : undefined;
};

interface AttachFrame {
  ref: LineEdgeRef;
  lf: LineFrame;
  /** The openings attached to this host, in attach order. */
  attached: FloorPlanEdgeSegmentSimple[];
  /** Alcove spans on this frame (primary edges only), for mouth absorption. */
  alcoves: { position: number; width: number }[];
}

/** A frame for every host in the room that has a line. */
const collectFrames = (shape: FloorPlanRoom): AttachFrame[] =>
  lineHosts(shape).flatMap(({ ref, segments }) => {
    const line = resolveLineRef(shape, ref);
    return line ? [{ ref, lf: line.lf, attached: [], alcoves: segments.filter(segment => segment.edgeSegmentType === 'alcove') }] : [];
  });

/** Where an opening surface lies along a frame: the span it covers, clipped to the frame, and its error (how far it is off the frame's line, plus its overhang past the frame's ends). */
interface Placement {
  frame: AttachFrame;
  error: number;
  from: number;
  to: number;
}

/** The surface from `a` to `b`, laid onto `frame`. */
function placementOn(frame: AttachFrame, a: Point, b: Point): Placement {
  const { lf } = frame;
  const [start, end] = [lineCoords(lf, a), lineCoords(lf, b)];
  const from = Math.min(start.along, end.along), to = Math.max(start.along, end.along);
  const overhang = Math.max(0, -from) + Math.max(0, to - lf.length);
  return { frame, error: Math.max(Math.abs(start.out), Math.abs(end.out)) + overhang, from: Math.max(0, from), to: Math.min(lf.length, to) };
}

/** The surface's placement on the frame it fits best: the first with the smallest error. */
const bestPlacement = (frames: AttachFrame[], a: Point, b: Point): Placement | null =>
  frames.reduce<Placement | null>((best, frame) => {
    const placement = placementOn(frame, a, b);
    return !best || placement.error < best.error ? placement : best;
  }, null);

/** An opening that lies across an alcove's mouth is the mouth itself, which the model already has. */
const liesAcrossMouth = ({ frame, from, to }: Placement): boolean =>
  frame.alcoves.some(alcove => Math.min(to, alcove.position + alcove.width) - Math.max(from, alcove.position) > 0.5 * (to - from));

/** The edge segment an opening surface of `kind` becomes where it is placed. */
function openingSegment(kind: EdgeSegmentType, surface: Surface, { from, to }: Placement, opts: ConversionOptions): FloorPlanEdgeSegmentSimple {
  const baseFt = surface.baseHeight * FT_PER_M;
  const width = to - from;
  return {
    edgeSegmentType: kind,
    position: from,
    width,
    openingBase: baseFt > 0.05 ? baseFt : undefined,
    openingHeight: surface.height * FT_PER_M,
    swing: kind === 'door' ? doorSwing(width, opts.roomKind) : undefined,
  };
}

/** Attach one opening surface; returns what happened (for the report). */
function attachOne(
  frames: AttachFrame[],
  kind: EdgeSegmentType,
  surface: Surface,
  toRoom: (p: Point) => Point,
  opts: ConversionOptions,
): 'attached' | 'absorbed' | 'discarded' {
  const best = bestPlacement(frames, toRoom(toFeetPoint(surface.start)), toRoom(toFeetPoint(surface.end)));
  if (!best || best.error > opts.maxAttachError || best.to - best.from < opts.minSurfaceLength) return 'discarded';
  if (liesAcrossMouth(best)) return 'absorbed';
  best.frame.attached.push(openingSegment(kind, surface, best, opts));
  return 'attached';
}

/**
 * The room with each opening surface attached to its best host, and every
 * framed host's segments in along-edge order. The frames are written back in
 * reverse: an alcove's walls and corners follow their primary edge in
 * `lineHosts`, so they are written while their refs still index the edge's
 * list, before sorting that list renumbers its alcoves.
 */
export function attachOpenings(
  shape: FloorPlanRoom,
  surfaces: { kind: EdgeSegmentType; list: Surface[] }[],
  toRoom: (p: Point) => Point,
  opts: ConversionOptions,
  report: ConversionReport,
): FloorPlanRoom {
  const frames = collectFrames(shape);
  for (const { kind, list } of surfaces) {
    for (const s of list) {
      const lenFt = Math.hypot(s.end.x - s.start.x, s.end.y - s.start.y) * FT_PER_M;
      if (lenFt < opts.minSurfaceLength) {
        report.culledSurfaceIds.push(s.id);
        continue;
      }
      const outcome = attachOne(frames, kind, s, toRoom, opts);
      if (outcome === 'absorbed') report.absorbedOpeningIds.push(s.id);
      if (outcome === 'discarded') report.discardedOpeningIds.push(s.id);
    }
  }
  return frames.reduceRight((room, { ref, attached }) => addSegmentsInPositionOrder(room, ref, attached), shape);
}
